import { useNavigate } from "@tanstack/react-router"
import React from "react"

import { trpc } from "../../lib/trpc"

interface StatCard {
  title: string
  value: string | number
  change?: string
  changeType?: "positive" | "negative" | "neutral"
  icon: string
}

const StatCard: React.FC<StatCard> = ({ title, value, change, changeType, icon }) => {
  const changeColor = changeType === "positive" ? "text-green-600" : changeType === "negative" ? "text-destructive" : ""

  return (
    <div className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium  dark:text-gray-400">{title}</p>
          <p className="text-2xl font-semibold  dark:text-white">{value}</p>
          {change && (
            <p className={`text-sm ${changeColor}`}>
              {changeType === "positive" ? "↗" : changeType === "negative" ? "↘" : "→"} {change}
            </p>
          )}
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  )
}

const CRMDashboard: React.FC = () => {
  const navigate = useNavigate()

  // Query for dashboard stats
  const { data: campaigns, isLoading: campaignsLoading } = trpc.crm.campaigns.list.useQuery({
    limit: 5,
    status: "active",
  })

  const { data: conversations, isLoading: conversationsLoading } = trpc.crm.conversations.list.useQuery({
    limit: 10,
    status: "active",
  })

  const handleNavigate = (path: string) => {
    navigate({ to: path })
  }

  // Calculate stats
  const totalCampaigns = campaigns?.total || 0
  const totalConversations = conversations?.total || 0
  const activeConversations = conversations?.conversations?.filter((c) => c.status === "active").length || 0
  const humanTakeovers = conversations?.conversations?.filter((c) => c.humanTakeover).length || 0

  const stats: StatCard[] = [
    {
      title: "Active Campaigns",
      value: totalCampaigns,
      icon: "🚀",
      change: "+2 this week",
      changeType: "positive",
    },
    {
      title: "Active Conversations",
      value: activeConversations,
      icon: "💬",
      change: `${totalConversations} total`,
      changeType: "neutral",
    },
    {
      title: "Human Takeovers",
      value: humanTakeovers,
      icon: "👨‍💻",
      change: "Last 24h",
      changeType: humanTakeovers > 5 ? "negative" : "neutral",
    },
    {
      title: "Response Rate",
      value: "73%",
      icon: "📈",
      change: "+5% vs last week",
      changeType: "positive",
    },
  ]

  const quickActions = [
    {
      title: "Start New Campaign",
      description: "Create a new negotiation campaign",
      icon: "🚀",
      onClick: () => handleNavigate("/crm/campaigns"),
      color: "bg-primary hover:",
    },
    {
      title: "Import Contacts",
      description: "Add contacts to your CRM",
      icon: "📥",
      onClick: () => handleNavigate("/crm/contacts"),
      color: "bg-green-500 hover:bg-green-600",
    },
    {
      title: "Review Inbox",
      description: "Check conversations needing attention",
      icon: "📬",
      onClick: () => handleNavigate("/crm/inbox"),
      color: "bg-orange-500 hover:bg-orange-600",
    },
    {
      title: "View Analytics",
      description: "Analyze campaign performance",
      icon: "📊",
      onClick: () => handleNavigate("/crm/analytics"),
      color: "bg-purple-500 hover:bg-purple-600",
    },
  ]

  return (
    <div className="h-full overflow-auto ">
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold  dark:text-white">CRM Dashboard</h1>
          <p className="mt-2  dark:text-gray-400">Manage your negotiation campaigns and conversations</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold  dark:text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                className={`${action.color} text-white p-6 rounded-lg shadow-sm transition-colors duration-200`}
              >
                <div className="text-3xl mb-2">{action.icon}</div>
                <h3 className="font-semibold text-lg">{action.title}</h3>
                <p className="text-sm opacity-90">{action.description}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Campaigns */}
          <div className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold  dark:text-white">Recent Campaigns</h2>
              <button
                onClick={() => handleNavigate("/crm/campaigns")}
                className=" dark:text-blue-400 hover:underline text-sm"
              >
                View All
              </button>
            </div>

            {campaignsLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Loading campaigns...</div>
              </div>
            ) : campaigns?.campaigns && campaigns.campaigns.length > 0 ? (
              <div className="space-y-3">
                {campaigns.campaigns.slice(0, 5).map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between p-3 border border-border dark:border-gray-700 rounded-lg"
                  >
                    <div>
                      <h3 className="font-medium  dark:text-white">{campaign.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {campaign.status} • {campaign.description || campaign.campaignContext || "No description"}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium  dark:text-white">0 conversations</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(campaign.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-4">No campaigns yet</div>
                <button
                  onClick={() => handleNavigate("/crm/campaigns")}
                  className=" text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Your First Campaign
                </button>
              </div>
            )}
          </div>

          {/* Recent Conversations */}
          <div className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold  dark:text-white">Recent Conversations</h2>
              <button
                onClick={() => handleNavigate("/crm/inbox")}
                className=" dark:text-blue-400 hover:underline text-sm"
              >
                View Inbox
              </button>
            </div>

            {conversationsLoading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Loading conversations...</div>
              </div>
            ) : conversations?.conversations && conversations.conversations.length > 0 ? (
              <div className="space-y-3">
                {conversations.conversations.slice(0, 5).map((conversation) => (
                  <div
                    key={conversation.id}
                    className="flex items-center justify-between p-3 border border-border dark:border-gray-700 rounded-lg"
                  >
                    <div>
                      <h3 className="font-medium  dark:text-white">
                        {(conversation.contact as any)?.name ||
                          (conversation.contact as any)?.email ||
                          "Unknown Contact"}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {(conversation.campaign as any)?.name || "Unknown Campaign"} • {conversation.platform}
                      </p>
                    </div>
                    <div className="text-right">
                      <div
                        className={`text-sm font-medium ${
                          conversation.status === "active"
                            ? "text-green-600"
                            : conversation.status === "human_takeover"
                              ? "text-orange-600"
                              : ""
                        }`}
                      >
                        {conversation.status}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {conversation.lastActivityAt
                          ? new Date(conversation.lastActivityAt).toLocaleDateString()
                          : "No activity"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-4">No conversations yet</div>
                <button
                  onClick={() => handleNavigate("/crm/contacts")}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Contacts to Start
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CRMDashboard
